import React, { useState, useMemo } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Download, 
  Share2, 
  Plus,
  AlertCircle,
  CheckCircle,
  Filter,
  ExternalLink,
  RefreshCw,
  Settings,
  Bell
} from 'lucide-react';
import { format, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isBefore, isAfter } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TaskStatus, TaskPriority } from '@/types';

interface CalendarEvent {
  id: string;
  title: string;
  type: 'task_deadline' | 'project_deadline' | 'meeting' | 'milestone';
  date: Date;
  time?: string;
  description?: string;
  priority: TaskPriority;
  status: TaskStatus;
  assignedTo?: string;
  projectId?: string;
  taskId?: string;
}

export const CalendarIntegration: React.FC = () => {
  const { projects, tasks } = useProject();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const [showCompleted, setShowCompleted] = useState(false);

  // Mock users
  const mockUsers = [
    { id: '1', name: 'Admin User', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face' },
    { id: '2', name: 'Manager User', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face' },
    { id: '3', name: 'Collaborator User', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face' }
  ];

  const getUserById = (userId: string) => mockUsers.find(u => u.id === userId);

  // Generate calendar events from tasks and projects
  const calendarEvents = useMemo(() => {
    const events: CalendarEvent[] = [];

    // Task deadlines
    tasks.forEach(task => {
      if (!showCompleted && task.status === TaskStatus.DONE) return;
      events.push({
        id: `task-${task.id}`,
        title: task.title,
        type: 'task_deadline',
        date: new Date(task.endDate),
        time: '17:00',
        description: task.description,
        priority: task.priority,
        status: task.status,
        assignedTo: task.assignedTo,
        projectId: task.projectId,
        taskId: task.id
      });
    });

    // Project deadlines
    projects.forEach(project => {
      events.push({
        id: `project-${project.id}`,
        title: `${project.name} - Deadline`,
        type: 'project_deadline',
        date: new Date(project.endDate),
        time: '23:59',
        description: project.description,
        priority: TaskPriority.HIGH,
        status: TaskStatus.IN_PROGRESS,
        projectId: project.id
      });
    });

    // Mock meetings and milestones
    events.push(
      {
        id: 'meeting-1',
        title: 'Reunião de Planejamento',
        type: 'meeting',
        date: new Date(Date.now() + 1000 * 60 * 60 * 24 * 2), // 2 days from now
        time: '14:00',
        description: 'Reunião semanal de planejamento do projeto',
        priority: TaskPriority.MEDIUM,
        status: TaskStatus.TODO
      },
      {
        id: 'milestone-1',
        title: 'Entrega MVP',
        type: 'milestone',
        date: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7), // 1 week from now
        time: '18:00',
        description: 'Entrega da primeira versão do MVP',
        priority: TaskPriority.URGENT,
        status: TaskStatus.IN_PROGRESS
      }
    );

    return events;
  }, [tasks, projects, showCompleted]);

  // Get events for selected date
  const selectedDateEvents = useMemo(() => {
    return calendarEvents.filter(event => 
      isSameDay(event.date, selectedDate)
    );
  }, [calendarEvents, selectedDate]);

  // Get events for current month
  const monthEvents = useMemo(() => {
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    return calendarEvents.filter(event => 
      isAfter(event.date, start) && isBefore(event.date, end)
    );
  }, [calendarEvents, selectedDate]);

  const getEventIcon = (type: CalendarEvent['type']) => {
    switch (type) {
      case 'task_deadline': return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'project_deadline': return <CalendarIcon className="w-4 h-4 text-purple-500" />;
      case 'meeting': return <Clock className="w-4 h-4 text-green-500" />;
      case 'milestone': return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default: return <CalendarIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getEventColor = (event: CalendarEvent) => {
    if (event.status === TaskStatus.DONE) return 'bg-green-100 text-green-800 border-green-300';
    if (isBefore(event.date, new Date()) && event.status !== TaskStatus.DONE) {
      return 'bg-red-100 text-red-800 border-red-300';
    }
    switch (event.priority) {
      case TaskPriority.URGENT: return 'bg-red-100 text-red-800 border-red-300';
      case TaskPriority.HIGH: return 'bg-orange-100 text-orange-800 border-orange-300';
      case TaskPriority.MEDIUM: return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case TaskPriority.LOW: return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const exportToCalendar = (format: 'ics' | 'google' | 'outlook') => {
    const events = selectedDateEvents.length > 0 ? selectedDateEvents : monthEvents;
    if (format === 'ics') {
      // Generate ICS file
      let icsContent = 'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Project Manager//Calendar//EN\n';
      events.forEach(event => {
        const startDate = format(event.date, 'yyyyMMdd');
        const startTime = event.time ? event.time.replace(':', '') + '00' : '000000';
        icsContent += `BEGIN:VEVENT\n`;
        icsContent += `DTSTART:${startDate}T${startTime}Z\n`;
        icsContent += `DTEND:${startDate}T${startTime}Z\n`;
        icsContent += `SUMMARY:${event.title}\n`;
        icsContent += `DESCRIPTION:${event.description || ''}\n`;
        icsContent += `UID:${event.id}@projectmanager.com\n`;
        icsContent += `END:VEVENT\n`;
      });
      icsContent += 'END:VCALENDAR';
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'project-calendar.ics';
      link.click();
    } else if (format === 'google') {
      // Google Calendar integration
      const event = events[0];
      if (event) {
        const startDate = format(event.date, 'yyyyMMdd');
        const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${startDate}/${startDate}&details=${encodeURIComponent(event.description || '')}`;
        window.open(url, '_blank');
      }
    }
  };

  const syncWithCalendar = (provider: 'google' | 'outlook' | 'apple') => {
    console.log(`Sincronizando com ${provider}`);
    // Mock sync functionality
    alert(`Sincronização com ${provider} iniciada! (Funcionalidade demo)`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Calendário</h1>
          <p className="text-gray-600 mt-1">Gerencie prazos e compromissos</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={() => setShowCompleted(!showCompleted)}>
            <Filter className="w-4 h-4 mr-2" />
            {showCompleted ? 'Ocultar' : 'Mostrar'} Concluídas
          </Button>
          <Button variant="outline" onClick={() => exportToCalendar('ics')}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
          <Button onClick={() => syncWithCalendar('google')} className="bg-gradient-to-r from-blue-500 to-purple-600">
            <RefreshCw className="w-4 h-4 mr-2" />
            Sincronizar
          </Button>
        </div>
      </div>

      {/* Integration Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Integrações de Calendário
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" onClick={() => syncWithCalendar('google')} className="justify-start">
              <ExternalLink className="w-4 h-4 mr-2" />
              Google Calendar
            </Button>
            <Button variant="outline" onClick={() => syncWithCalendar('outlook')} className="justify-start">
              <ExternalLink className="w-4 h-4 mr-2" />
              Outlook
            </Button>
            <Button variant="outline" onClick={() => syncWithCalendar('apple')} className="justify-start">
              <ExternalLink className="w-4 h-4 mr-2" />
              Apple Calendar
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  {format(selectedDate, 'MMMM yyyy', { locale: ptBR })}
                </CardTitle>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm" onClick={() => setViewMode('month')}>
                    Mês
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setViewMode('week')}>
                    Semana
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setViewMode('day')}>
                    Dia
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-md border"
                locale={ptBR}
              />
              {/* Mini events preview */}
              <div className="mt-4 space-y-2">
                {eachDayOfInterval({ 
                  start: startOfMonth(selectedDate), 
                  end: endOfMonth(selectedDate) 
                }).map(day => {
                  const dayEvents = calendarEvents.filter(event => isSameDay(event.date, day));
                  if (dayEvents.length === 0) return null;
                  return (
                    <div key={day.toString()} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium">
                        {format(day, 'dd/MM', { locale: ptBR })}
                      </span>
                      <div className="flex items-center space-x-1">
                        {dayEvents.slice(0, 3).map(event => (
                          <div key={event.id} className="w-2 h-2 rounded-full bg-blue-500" />
                        ))}
                        {dayEvents.length > 3 && (
                          <span className="text-xs text-gray-500">+{dayEvents.length - 3}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Events for Selected Date */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                {format(selectedDate, 'dd/MM/yyyy', { locale: ptBR })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {selectedDateEvents.length > 0 ? (
                  selectedDateEvents.map(event => {
                    const assignedUser = event.assignedTo ? getUserById(event.assignedTo) : null;
                    const isOverdue = isBefore(event.date, new Date()) && event.status !== TaskStatus.DONE;
                    return (
                      <div key={event.id} className={`p-3 rounded-lg border ${getEventColor(event)}`}>
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            {getEventIcon(event.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-sm">{event.title}</h4>
                              {isOverdue && (
                                <Badge variant="destructive" className="text-xs">
                                  Atrasado
                                </Badge>
                              )}
                            </div>
                            {event.time && (
                              <p className="text-xs text-gray-600 mt-1">{event.time}</p>
                            )}
                            {event.description && (
                              <p className="text-xs text-gray-600 mt-1">{event.description}</p>
                            )}
                            {assignedUser && (
                              <div className="flex items-center space-x-2 mt-2">
                                <Avatar className="w-4 h-4">
                                  <AvatarImage src={assignedUser.avatar} alt={assignedUser.name} />
                                  <AvatarFallback className="text-xs">
                                    {assignedUser.name.split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-xs text-gray-600">{assignedUser.name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nenhum evento para esta data</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Ações Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start">
                  <Plus className="w-4 h-4 mr-2" />
                  Agendar Reunião
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Bell className="w-4 h-4 mr-2" />
                  Definir Lembrete
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartilhar Calendário
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => exportToCalendar('google')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Abrir no Google
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Próximos Eventos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {calendarEvents
                  .filter(event => isAfter(event.date, new Date()))
                  .sort((a, b) => a.date.getTime() - b.date.getTime())
                  .slice(0, 5)
                  .map(event => (
                    <div key={event.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center space-x-2">
                        {getEventIcon(event.type)}
                        <div>
                          <div className="text-sm font-medium">{event.title}</div>
                          <div className="text-xs text-gray-500">
                            {format(event.date, 'dd/MM', { locale: ptBR })}
                          </div>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {event.priority}
                      </Badge>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};